"use client";

import { Mail, Phone, Wrench } from "lucide-react";
import { useSiteSettings } from "@/components/site-settings-provider";

export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { settings, status } = useSiteSettings();
  const isOffline = status === "offline";
  const isEnabled = settings.maintenance.enabled || isOffline;

  if (!isEnabled) {
    return <>{children}</>;
  }

  const contactEmail = settings.maintenance.contactEmail.trim() || settings.general.contactEmail.trim();
  const contactPhone = settings.maintenance.contactPhone.trim() || settings.general.contactPhone.trim();
  const title = settings.maintenance.title.trim() || "Under Maintenance";
  const message = settings.maintenance.message.trim() || "We are currently performing scheduled maintenance. Please check back shortly.";
  const contactTitle = settings.maintenance.contactTitle.trim() || "Need immediate assistance?";
  const contactDescription = settings.maintenance.contactDescription.trim() || "If your request is urgent, reach out to our team using the contact details below.";

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.18),_transparent_45%),linear-gradient(180deg,_#f8fbff_0%,_#eef4ff_45%,_#fff7ed_100%)] px-6 py-16 text-slate-950">
      <div className="absolute inset-0 opacity-60">
        <div className="absolute left-[8%] top-20 h-40 w-40 rounded-full bg-cyan-200/60 blur-3xl" />
        <div className="absolute bottom-16 right-[10%] h-48 w-48 rounded-full bg-amber-200/70 blur-3xl" />
      </div>

      <section className="relative z-10 w-full max-w-3xl rounded-[32px] border border-white/70 bg-white/85 p-8 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur xl:p-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900">
          <Wrench className="h-4 w-4" />
          {isOffline ? "Backend unavailable" : "Maintenance mode enabled"}
        </div>

        <div className="mt-6 space-y-4">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-700">iRoot Technologies</p>
          <h1 className="max-w-2xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">{title}</h1>
          <p className="max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">{message}</p>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-slate-50/90 p-5">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Status</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900">Under Maintenance</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">The public website is temporarily unavailable while service is restored or scheduled updates are completed.</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50/90 p-5">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Support</p>
            <p className="mt-3 text-2xl font-semibold text-slate-900">{contactTitle}</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">{contactDescription}</p>
          </div>
        </div>

        {(contactEmail || contactPhone) ? (
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {contactEmail ? (
              <a href={`mailto:${contactEmail}`} className="group rounded-3xl border border-cyan-200 bg-cyan-50/70 p-5 hover:border-cyan-300 hover:bg-cyan-50">
                <div className="flex items-center gap-3 text-cyan-900">
                  <Mail className="h-5 w-5" />
                  <span className="text-sm font-semibold uppercase tracking-[0.18em]">Email</span>
                </div>
                <p className="mt-4 text-lg font-semibold text-slate-900 group-hover:text-cyan-900">{contactEmail}</p>
              </a>
            ) : null}

            {contactPhone ? (
              <a href={`tel:${contactPhone}`} className="group rounded-3xl border border-amber-200 bg-amber-50/70 p-5 hover:border-amber-300 hover:bg-amber-50">
                <div className="flex items-center gap-3 text-amber-900">
                  <Phone className="h-5 w-5" />
                  <span className="text-sm font-semibold uppercase tracking-[0.18em]">Phone</span>
                </div>
                <p className="mt-4 text-lg font-semibold text-slate-900 group-hover:text-amber-900">{contactPhone}</p>
              </a>
            ) : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}